import { currentEnv } from "@shared/utils/fullscriptEnv/fullscriptEnv";

const APPLICATION_ID = "5f28ea57-4a61-49df-ab33-cd186972d84d";
const CLIENT_TOKEN = "pubc08528e72f3b132700be070ebb5df20d";

interface DatadogRumConfig {
  enabled: boolean;
  region: string | null;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  userId?: string | null;
  roleId?: string | null;
}

// Mirrors the server-injected `datadog-rum-config` meta tag, written by both the
// legacy `DatadogRumHelper` and the Remix `Document`. Decoding matches the
// `encodeIntoMetaTag` / `decodeFromMetaTag` contract: `decodeURIComponent(atob(content))`.
const readConfig = (): DatadogRumConfig | null => {
  const metaTag = document.querySelector('meta[name="datadog-rum-config"]');

  if (!metaTag) return null;

  try {
    return JSON.parse(
      decodeURIComponent(atob(metaTag.getAttribute("content") || ""))
    ) as DatadogRumConfig;
  } catch {
    return null;
  }
};

/**
 * Datadog RUM initialization for the client.
 *
 * Gated entirely on the server-injected `datadog_rum_enabled` flag (sourced from
 * the per-region SSM env var), so rollout is a pure ops toggle with no frontend
 * redeploy. The SDK is dynamically imported only when the flag is on, keeping it
 * out of the bundle for regions where RUM is disabled.
 *
 * Fire-and-forget: call sites do not await this.
 */
const clientInitDatadogRum = async (): Promise<void> => {
  const config = readConfig();

  if (!config?.enabled) return;

  // The RUM SDK loads as a lazy chunk. A failed load (flaky mobile networks,
  // aborted navigation) can reject or resolve to a non-destructurable value, so
  // swallow it here: RUM is telemetry and must never break the page or emit an
  // unhandled rejection.
  try {
    const datadogModule = await import("@datadog/browser-rum");
    const datadogRum = datadogModule?.datadogRum;

    if (!datadogRum) return;

    datadogRum.init({
      applicationId: APPLICATION_ID,
      clientToken: CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "hw-admin",
      env: currentEnv(),
      version: process.env.CI_COMMIT_SHA || "local",
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
    });

    if (config.region) {
      datadogRum.setGlobalContextProperty("region", config.region);
    }

    if (config.userId) {
      datadogRum.setUser({ id: config.userId, roleId: config.roleId ?? undefined });
    }
  } catch {
    // Datadog RUM failed to load; continue without client telemetry.
  }
};

export { clientInitDatadogRum };
